import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, XCircle, Loader2, ArrowRight, Mail } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterBestaetigt = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  // Initialize state based on whether token exists
  const [status, setStatus] = useState(() => token ? 'loading' : 'no-token');
  const [message, setMessage] = useState(() => token ? '' : 'Kein Bestätigungstoken gefunden.');
  const hasCalledRef = useRef(false);

  useEffect(() => {
    // Only run once and only if token exists
    if (!token || hasCalledRef.current) return;
    hasCalledRef.current = true;
    
    // Use IIFE to handle async
    (async () => {
      try {
        const response = await axios.get(`${API_URL}/api/newsletter/confirm/${token}`);
        
        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message);
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Ein Fehler ist aufgetreten.');
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Ungültiger oder abgelaufener Bestätigungslink.');
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-lg mx-auto">
        <Card className="border-2 border-gray-200 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center">
            {/* Loading State */}
            {status === 'loading' && (
              <>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Anmeldung wird bestätigt...
                </h1>
                <p className="text-gray-600">
                  Bitte warte einen Moment.
                </p>
              </>
            )}

            {/* Success State */}
            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Newsletter bestätigt!
                </h1>
                <p className="text-gray-600 mb-8">
                  {message}
                </p>
                <div className="space-y-3">
                  <Link to="/marktplatz" className="block">
                    <Button 
                      size="lg" 
                      className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
                      data-testid="newsletter-confirmed-marketplace-btn"
                    >
                      Zum Marktplatz
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                  <Link to="/" className="block">
                    <Button variant="outline" size="lg" className="w-full" data-testid="newsletter-confirmed-home-btn">
                      Zur Startseite
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {/* Error State */}
            {(status === 'error' || status === 'no-token') && (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Bestätigung fehlgeschlagen
                </h1>
                <p className="text-gray-600 mb-8">
                  {message}
                </p>
                <div className="space-y-3">
                  <Link to="/" className="block">
                    <Button 
                      size="lg" 
                      className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
                      data-testid="newsletter-error-home-btn"
                    >
                      Zur Startseite
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                  <Link to="/kontakt" className="block">
                    <Button variant="outline" size="lg" className="w-full" data-testid="newsletter-error-contact-btn">
                      <Mail className="mr-2 w-5 h-5" />
                      Kontakt aufnehmen
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewsletterBestaetigt;
